/**
 * Push-notification tap and foreground-refresh handling (src/platform/).
 *
 * Contract: every capability has a working web implementation; native
 * branches load Capacitor plugins dynamically and MUST keep the web
 * fallback. On web this is a complete no-op — web push is deferred by epic
 * #993, so there is nothing to tap or resume.
 *
 * Companion to `push.ts` (registration) — this module covers the two things
 * registration doesn't: routing a tapped notification to the right screen,
 * and refreshing live data when the app returns to the foreground (#2567).
 */

import type { PluginListenerHandle } from '@capacitor/core'

import { resolveNotificationRoute, type NotificationRoute } from '@/lib/notificationRoute'
import { getPlatform, isNativePlatform } from './detection'

/** The shape a delivered push notification's `data` payload carries. */
interface PushNotificationData {
  type: string
  entity_type: string | null
  entity_id: string | null
}

/** The two in-notification action buttons registered for AGENT_APPROVAL (AppDelegate.swift). */
export type ApprovalActionId = 'approve' | 'reject'

const APPROVAL_ACTION_IDS: readonly ApprovalActionId[] = ['approve', 'reject']

export interface PushNotificationHandlers {
  /** A delivered notification was tapped — resolve it the same way an in-app click does. */
  onTap: (route: NotificationRoute) => void
  /**
   * An in-notification approve/reject action button was tapped (rather than
   * the notification body itself). Only fires for an `agent_approval`
   * notification carrying an entity_id — anything else falls back to onTap,
   * same as a plain tap.
   */
  onApprovalAction?: (action: ApprovalActionId, approvalId: string) => void
  /** A push notification arrived while the app was in the foreground. */
  onForegroundReceive?: () => void
  /** The app itself returned to the foreground, for any reason. */
  onAppForeground?: () => void
}

function routableFromPushData(data: unknown): PushNotificationData | null {
  if (!data || typeof data !== 'object') return null
  const record = data as Record<string, unknown>
  if (typeof record.type !== 'string') return null
  return {
    type: record.type,
    entity_type: typeof record.entity_type === 'string' ? record.entity_type : null,
    entity_id: typeof record.entity_id === 'string' ? record.entity_id : null,
  }
}

/** The shape both @capacitor/push-notifications and the native `PushAction`
 * plugin (Android only — see ApprovalMessagingService.java/PushActionPlugin.java)
 * deliver an action-performed event as. */
interface ActionPerformedEvent {
  actionId: string
  notification: { data: unknown }
}

function dispatchActionPerformed(
  event: ActionPerformedEvent,
  handlers: PushNotificationHandlers
): void {
  const routable = routableFromPushData(event.notification.data)
  if (!routable) return

  const actionId = event.actionId as ApprovalActionId | 'tap' | 'dismiss' | string
  if (
    handlers.onApprovalAction &&
    routable.entity_type === 'agent_approval' &&
    routable.entity_id &&
    (APPROVAL_ACTION_IDS as readonly string[]).includes(actionId)
  ) {
    handlers.onApprovalAction(actionId as ApprovalActionId, routable.entity_id)
    return
  }

  handlers.onTap(resolveNotificationRoute(routable))
}

/** Minimal API surface of the native-only `PushAction` plugin (Android). */
interface PushActionPluginApi {
  addListener(
    eventName: 'pushActionPerformed',
    listener: (event: ActionPerformedEvent) => void
  ): Promise<PluginListenerHandle>
}

/**
 * Wire push-tap routing and foreground refresh for as long as the returned
 * disposer is not called. No-op (and resolves to a no-op disposer) on web.
 */
export async function initPushNotificationHandlers(
  handlers: PushNotificationHandlers
): Promise<() => void> {
  if (!isNativePlatform()) return () => undefined

  const disposers: (() => void)[] = []

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')

    const tapHandle = await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (event) => dispatchActionPerformed(event, handlers)
    )
    disposers.push(() => void tapHandle.remove())

    if (handlers.onForegroundReceive) {
      const receiveHandle = await PushNotifications.addListener('pushNotificationReceived', () => {
        handlers.onForegroundReceive?.()
      })
      disposers.push(() => void receiveHandle.remove())
    }
  } catch {
    // Plugin unavailable — nothing to wire.
  }

  // Android only: @capacitor/push-notifications never attaches action
  // buttons itself, so ApprovalMessagingService.java builds the system
  // notification natively and PushActionPlugin.java surfaces the tapped
  // action through this plugin instead (see AndroidManifest.xml's
  // tools:node="remove" on the default MessagingService).
  if (getPlatform() === 'android') {
    try {
      const { registerPlugin } = await import('@capacitor/core')
      const pushAction = registerPlugin<PushActionPluginApi>('PushAction')
      const actionHandle = await pushAction.addListener('pushActionPerformed', (event) =>
        dispatchActionPerformed(event, handlers)
      )
      disposers.push(() => void actionHandle.remove())
    } catch {
      // Plugin unavailable — nothing to wire.
    }
  }

  if (handlers.onAppForeground) {
    try {
      const { App } = await import('@capacitor/app')
      const resumeHandle: PluginListenerHandle = await App.addListener(
        'appStateChange',
        (state) => {
          if (state.isActive) handlers.onAppForeground?.()
        }
      )
      disposers.push(() => void resumeHandle.remove())
    } catch {
      // Plugin unavailable.
    }
  }

  return () => disposers.forEach((dispose) => dispose())
}
